import { useMutation, useQueryClient } from '@tanstack/react-query';
import { firstValueFrom } from 'rxjs';

import { COWORKER_ATTENDANCE_QUERY_KEY } from 'data/queries/useCoworkerAttendanceQuery.hook';
import { COWORKER_GROUPS_QUERY_KEY } from 'data/queries/useCoworkerGroupsQuery.hook';
import { useResourceBookingServiceClient } from 'hooks/use-resource-booking-service-client.hook';
import {
  CoworkerGroupResponse,
  CreateCoworkerGroupBody,
  UpdateCoworkerGroupBody,
} from 'store/api-client/coworker-types';

interface UpdateCoworkerGroupVariables {
  groupUuid: string;
  body: UpdateCoworkerGroupBody;
}

export interface UseCoworkerGroupMutationsResult {
  createGroup: (body: CreateCoworkerGroupBody) => Promise<CoworkerGroupResponse>;
  updateGroup: (variables: UpdateCoworkerGroupVariables) => Promise<CoworkerGroupResponse>;
  deleteGroup: (groupUuid: string) => Promise<void>;
  /** A create or an update is in flight — the builder's Save is disabled meanwhile. */
  isSaving: boolean;
  isDeleting: boolean;
}

/**
 * Create / rename+re-member / delete one of the requester's coworker groups (§C3). Every success
 * invalidates the building's groups list, so the chips and the map's active group follow the
 * server's version rather than an optimistic one — membership is filtered server-side (invisible
 * uuids are dropped), so the response can legitimately differ from what was sent.
 *
 * The building's attendance is invalidated too: a group-scoped week is keyed by the group's uuid,
 * which a membership change leaves untouched, so without this the banner's "N of M" would keep
 * counting the old members.
 */
export const useCoworkerGroupMutations = (): UseCoworkerGroupMutationsResult => {
  const { getClient, buildingUuid } = useResourceBookingServiceClient();
  const queryClient = useQueryClient();

  const invalidateGroups = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [COWORKER_GROUPS_QUERY_KEY, buildingUuid] }),
      queryClient.invalidateQueries({ queryKey: [COWORKER_ATTENDANCE_QUERY_KEY, buildingUuid] }),
    ]);

  const create = useMutation({
    mutationFn: (body: CreateCoworkerGroupBody) =>
      firstValueFrom(getClient().createCoworkerGroup(buildingUuid as string, body)),
    onSuccess: invalidateGroups,
  });

  const update = useMutation({
    mutationFn: ({ groupUuid, body }: UpdateCoworkerGroupVariables) =>
      firstValueFrom(getClient().updateCoworkerGroup(buildingUuid as string, groupUuid, body)),
    onSuccess: invalidateGroups,
  });

  const remove = useMutation({
    mutationFn: (groupUuid: string) =>
      firstValueFrom(getClient().deleteCoworkerGroup(buildingUuid as string, groupUuid)),
    onSuccess: invalidateGroups,
  });

  return {
    createGroup: create.mutateAsync,
    updateGroup: update.mutateAsync,
    deleteGroup: remove.mutateAsync,
    isSaving: create.isPending || update.isPending,
    isDeleting: remove.isPending,
  };
};
